import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/slices/authSlice';

/**
 * Auto-logout on inactivity.
 *
 * The backend uses opaque DRF tokens (no JWT `exp` claim to decode),
 * so we can't know server-side expiry client-side. Instead we enforce
 * a sliding inactivity window: any user interaction resets the timer;
 * after IDLE_LIMIT_MS of no interaction the session is ended and the
 * user is redirected to /login with ?expired=1.
 *
 * The reactive 401 interceptor in services/api.ts remains the backstop
 * for the case where the server invalidates the token early.
 */

// 30 minutes of inactivity
const IDLE_LIMIT_MS = 30 * 60 * 1000;
// Persisted across reloads so closing/reopening a tab doesn't reset it
const LAST_ACTIVITY_KEY = 'emis_last_activity';

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

export function useSessionTimeout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const endSession = useCallback(async () => {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    await dispatch(logout());
    navigate('/login?expired=1', { replace: true });
  }, [dispatch, navigate]);

  const armTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
    const now = Date.now();

    // If we were away longer than the limit, log out immediately
    if (last && now - last >= IDLE_LIMIT_MS) {
      endSession();
      return;
    }

    const remaining = last ? IDLE_LIMIT_MS - (now - last) : IDLE_LIMIT_MS;
    timerRef.current = setTimeout(endSession, remaining);
  }, [endSession]);

  const recordActivity = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    armTimer();
  }, [armTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Seed activity on mount and start the timer
    recordActivity();

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, recordActivity, { passive: true })
    );

    // Re-evaluate when the tab regains focus (covers sleep/long background)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') armTimer();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, recordActivity)
      );
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, recordActivity, armTimer]);
}
